import { useState } from "react";
import Topbar from "@/components/Topbar";
import {
  LoadFlowCard, ShortCircuitCard, MotorStartingCard, TCCCard,
  HarmonicsCard, BatterySizingCard, GroundingCard,
  TransientStabilityCard, ReliabilityCard, CableAmpacityCard, OpfCard,
} from "@/components/studies/StudyCards";
import {
  ChartLine, Lightning, Fan, WaveSine, Gauge, BatteryFull, TreeStructure,
  Waveform, ShieldCheck, LineSegments, ChartPieSlice,
} from "@phosphor-icons/react";

/* v2.98 · Power System Studies hub — 11 studies. */

const STUDIES = [
  { id: "load-flow", label: "Load flow", icon: ChartLine, Card: LoadFlowCard },
  { id: "short-circuit", label: "Short-circuit", icon: Lightning, Card: ShortCircuitCard },
  { id: "motor-starting", label: "Motor starting", icon: Fan, Card: MotorStartingCard },
  { id: "tcc", label: "TCC coord.", icon: TreeStructure, Card: TCCCard },
  { id: "harmonics", label: "Harmonics", icon: WaveSine, Card: HarmonicsCard },
  { id: "battery", label: "Battery sizing", icon: BatteryFull, Card: BatterySizingCard },
  { id: "grounding", label: "Grounding", icon: Gauge, Card: GroundingCard },
  { id: "transient-stability", label: "Transient stability", icon: Waveform, Card: TransientStabilityCard },
  { id: "reliability", label: "Reliability", icon: ShieldCheck, Card: ReliabilityCard },
  { id: "cable-ampacity", label: "Cable ampacity", icon: LineSegments, Card: CableAmpacityCard },
  { id: "opf", label: "OPF · dispatch", icon: ChartPieSlice, Card: OpfCard },
];

export default function StudiesPage() {
  const [tab, setTab] = useState("load-flow");
  const active = STUDIES.find((s) => s.id === tab) || STUDIES[0];
  const Card = active.Card;

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag">// power system studies</div>
        <h1 className="font-display text-4xl md:text-5xl font-black mt-1">Studies</h1>
        <p className="text-gray-400 mt-2 max-w-3xl">
          ETAP-lite MVP — <b>eleven</b> power-system studies powered by IEC 60909, IEEE C37.112,
          IEEE 80, IEEE 485, IEEE 519, IEEE 141, IEEE 1366 and NEC 310.16.
          Seven of them (LF · SC · MS · TCC · HM · BAT · GND) plus Transient stability, Reliability and Cable ampacity
          ship with a graphical mode; OPF adds a network mode with three flavours (lossless · DC+losses · AC / Newton-Raphson).
          Every run can be <span className="text-[#00E5FF] font-mono">saved to a project</span>,
          exported as a stampable PE PDF, or rolled into a
          <span className="text-[#00E5FF] font-mono"> Compliance Bundle</span> from the project view.
        </p>

        <div className="mt-6 border-b border-white/10 flex flex-wrap items-center gap-x-4 gap-y-1"
             role="tablist" data-testid="studies-tabs">
          {STUDIES.map((s) => {
            const Icon = s.icon;
            const on = tab === s.id;
            return (
              <button key={s.id} onClick={() => setTab(s.id)}
                className={`inline-flex items-center gap-1.5 py-3 px-2 text-sm font-mono transition border-b-2 ${on ? "text-[#00E5FF] border-[#00E5FF]" : "text-gray-400 border-transparent hover:text-white"}`}
                data-testid={`studies-tab-${s.id}`}>
                <Icon size={14} weight={on ? "fill" : "regular"} /> {s.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8 border border-white/10 bg-[#12151C] p-6">
          <Card />
        </div>

        <div className="mt-6 text-[10px] font-mono text-gray-500">
          MVP disclaimer: results are engineering estimates. Final designs must be reviewed and stamped by a licensed P.E.
        </div>
      </div>
    </div>
  );
}
